import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import ReactPaginate from "react-paginate";
import { SyncLoader } from "react-spinners";

type CustomTableProps = {
  data: {
    columns: { label: string; field: string }[];
    rows: any;
  };
  handleSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  actionButton?: React.ReactNode;
  noRecordText?: string;
  loading?: boolean;
  totalPages?: number;
  itemsPerPage?: number;
  currentPage?: number;
  onPageChange?: (selected: number) => void;
  onLimitChange?: (limit: number) => void;
  showFooter?: boolean;
  filter?: React.ReactNode;
  onRowClick?: (row: any) => void;
};

const CustomTable: React.FC<CustomTableProps> = ({
  data,
  handleSearch,
  actionButton,
  noRecordText,
  loading,
  totalPages,
  itemsPerPage,
  currentPage,
  onPageChange,
  onLimitChange,
  showFooter,
  filter,
  onRowClick,
}) => {
  const { columns, rows } = data;

  const handlePageChange = (selectedItem: { selected: number }) => {
    onPageChange && onPageChange(selectedItem.selected);
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const limit = Number(e.target.value);
    onLimitChange && onLimitChange(limit);
    onPageChange && onPageChange(0); // Reset to the first page when limit changes
  };

  console.log(totalPages);

  return (
    <div className="bg-white p-4 sm:p-6 rounded-3xl overflow-hidden w-full">
      {/* Search and Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        {handleSearch && (
          <input
            type="text"
            placeholder="Search..."
            className="w-full sm:w-[250px] border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={handleSearch}
          />
        )}
        {actionButton}
      </div>
      {/* Table Container */}
      <div className="relative min-h-[300px] max-h-[400px]">
        {loading ? (
          <div className="py-12 w-full flex items-center justify-center">
            <SyncLoader size={16} color="#3F2EC8" />
          </div>
        ) : rows?.length > 0 ? (
          <div className="overflow-x-auto min-h-[300px] max-h-[400px]">
            <table className="min-w-full text-left text-sm text-gray-700">
              <thead className="bg-gray-100 text-sm text-gray-500 sticky top-0 ">
                <tr>
                  {columns.map((column, index) => (
                    <th
                      key={index}
                      className="py-2 px-4 text-xs text-gray-400 bg-gray-100 border-b border-gray-200 whitespace-nowrap"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {rows.map((row: any, rowIndex: number) => (
                  <tr
                    key={rowIndex}
                    className={`border-t text-xs ${
                      onRowClick
                        ? "hover:bg-purple-50 cursor-pointer transition-colors duration-150"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className="px-4 py-2 whitespace-nowrap"
                        onClick={
                          (column.field.toLowerCase().includes("action") ||
                            column.field === "checkbox") &&
                          onRowClick
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {row[column.field]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[200px] text-center px-4">
            <img
              src="/assets/images/svg/no-record.svg"
              alt="No Records"
              className="w-24 h-24 sm:w-32 sm:h-32"
            />
            <div className="text-gray-500 text-base sm:text-lg mt-2">
              {noRecordText ?? "No records found"}
            </div>
          </div>
        )}
      </div>

      {/* Footer: Pagination and Limit */}
      {showFooter && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 gap-4">
          {/* Items per page */}
          <div className="flex items-center space-x-2">
            <label htmlFor="items-per-page" className="text-sm text-gray-500">
              Items per page:
            </label>
            <select
              id="items-per-page"
              className="border border-[#E2E2E2] bg-transparent outline-none rounded-md px-2 py-1 text-sm text-gray-700"
              value={itemsPerPage}
              onChange={handleLimitChange}
            >
              {[10, 20, 50].map((limit) => (
                <option key={limit} value={limit}>
                  {limit} rows
                </option>
              ))}
            </select>
          </div>

          {/* Pagination */}
          <ReactPaginate
            previousLabel={<ChevronLeft className="w-5 h-5 text-primary" />}
            nextLabel={<ChevronRight className="w-5 h-5 text-primary" />}
            breakLabel={<span className="px-2 text-primary">...</span>}
            pageCount={totalPages || 1}
            onPageChange={handlePageChange}
            containerClassName="flex flex-wrap items-center space-x-1"
            pageClassName="px-3 py-1 rounded-md cursor-pointer text-sm text-gray-600 hover:bg-gray-200"
            activeClassName="bg-primary text-white font-semibold"
            previousClassName="rounded-md cursor-pointer text-sm text-white hover:bg-gray-200 border border-[#E2E2E2] bg-[#D9D9D9]"
            nextClassName="rounded-md cursor-pointer text-sm text-white hover:bg-gray-200 border border-[#E2E2E2] bg-[#D9D9D9]"
            disabledClassName="text-gray-300 cursor-not-allowed"
            forcePage={currentPage}
          />
        </div>
      )}
    </div>
  );
};

export default CustomTable;
